import type { DashboardSettingFieldConfig } from '../dashboardSettingNames';
import {
  parseDecimalSetting,
  parseIntegerSetting,
  parsePercentSetting,
  parseUsdSetting,
} from './dashboardSettingParsers';

export function validateFieldValue(
  field: DashboardSettingFieldConfig,
  raw: string
): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return 'Value is required.';

  switch (field.kind) {
    case 'year':
      if (!/^\d{4}$/.test(trimmed)) return 'Enter a 4-digit year.';
      return null;
    case 'percent': {
      const n = parsePercentSetting(trimmed, NaN);
      if (!Number.isFinite(n)) return 'Enter a whole number.';
      if (field.min != null && n < field.min) return `Must be at least ${field.min}.`;
      if (field.max != null && n > field.max) return `Must be at most ${field.max}.`;
      return null;
    }
    case 'integer': {
      const n = parseIntegerSetting(trimmed, NaN);
      if (!Number.isFinite(n)) return 'Enter a whole number.';
      if (field.min != null && n < field.min) return `Must be at least ${field.min}.`;
      if (field.max != null && n > field.max) return `Must be at most ${field.max}.`;
      return null;
    }
    case 'decimal': {
      const n = parseDecimalSetting(trimmed, NaN);
      if (!Number.isFinite(n)) return 'Enter a valid number.';
      if (n <= 0) return 'Must be greater than 0.';
      if (field.min != null && n < field.min) return `Must be at least ${field.min}.`;
      return null;
    }
    case 'usd': {
      const n = parseUsdSetting(trimmed, NaN);
      if (!Number.isFinite(n)) return 'Enter a whole number.';
      if (field.min != null && n < field.min) return `Must be at least ${field.min}.`;
      return null;
    }
    default:
      return null;
  }
}

export function validateSectionCrossFields(
  values: Record<string, string>
): Record<string, string> {
  const errors: Record<string, string> = {};

  const medium = parseIntegerSetting(values['Risk Triage Anomaly Score Medium Threshold'], NaN);
  const high = parseIntegerSetting(values['Risk Triage Anomaly Score High Threshold'], NaN);
  if (Number.isFinite(medium) && Number.isFinite(high) && medium >= high) {
    errors['Risk Triage Anomaly Score Medium Threshold'] =
      'Medium threshold must be less than high threshold.';
  }

  const warnCompleteness = parsePercentSetting(
    values['School Profile Completeness Warning Threshold'],
    NaN
  );
  const goodCompleteness = parsePercentSetting(
    values['School Profile Completeness Good Threshold'],
    NaN
  );
  if (
    Number.isFinite(warnCompleteness) &&
    Number.isFinite(goodCompleteness) &&
    warnCompleteness >= goodCompleteness
  ) {
    errors['School Profile Completeness Warning Threshold'] =
      'Warning threshold must be less than good threshold.';
  }

  const budgetWarn = parsePercentSetting(values['Budget Law A+B Warning Band Percent'], NaN);
  const budgetMin = parsePercentSetting(values['Budget Law A+B Minimum Percent'], NaN);
  if (Number.isFinite(budgetWarn) && Number.isFinite(budgetMin) && budgetWarn >= budgetMin) {
    errors['Budget Law A+B Warning Band Percent'] =
      'Warning band must be less than minimum percent.';
  }

  const highRatio = parseDecimalSetting(
    values['Teacher Equity Spread Severity High Ratio'],
    NaN
  );
  const veryHighRatio = parseDecimalSetting(
    values['Teacher Equity Spread Severity Very High Ratio'],
    NaN
  );
  if (Number.isFinite(highRatio) && Number.isFinite(veryHighRatio) && highRatio >= veryHighRatio) {
    errors['Teacher Equity Spread Severity High Ratio'] =
      'High ratio must be less than very high ratio.';
  }

  const highAmount = parseUsdSetting(
    values['Teacher Equity Spread Severity High Amount USD'],
    NaN
  );
  const veryHighAmount = parseUsdSetting(
    values['Teacher Equity Spread Severity Very High Amount USD'],
    NaN
  );
  if (
    Number.isFinite(highAmount) &&
    Number.isFinite(veryHighAmount) &&
    highAmount >= veryHighAmount
  ) {
    errors['Teacher Equity Spread Severity High Amount USD'] =
      'High amount must be less than very high amount.';
  }

  return errors;
}

export function validateSectionFields(
  fields: DashboardSettingFieldConfig[],
  draftValues: Record<string, string>
): Record<string, string> {
  const errors: Record<string, string> = {};

  for (const field of fields) {
    const err = validateFieldValue(field, draftValues[field.name] ?? '');
    if (err) errors[field.name] = err;
  }

  const cross = validateSectionCrossFields(draftValues);
  for (const [name, msg] of Object.entries(cross)) {
    if (fields.some((f) => f.name === name)) {
      errors[name] = msg;
    }
  }

  return errors;
}

export function serializeFieldValue(field: DashboardSettingFieldConfig, raw: string): string {
  const trimmed = raw.trim();
  switch (field.kind) {
    case 'percent':
    case 'integer':
    case 'usd':
      return String(parseIntegerSetting(trimmed, parseIntegerSetting(field.defaultValue, 0)));
    case 'decimal':
      return String(parseDecimalSetting(trimmed, parseDecimalSetting(field.defaultValue, 0)));
    default:
      return trimmed;
  }
}
